/* Reference legend for the Kohd Writer page: every circuit part and lexicon
   symbol, drawn with the module's own shapes so it always matches the output. */

import { useStrings } from '../locales';
import { CONSTRUCT_LABELS, ConstructType, LEXICON_PHRASES, LEXICON_WORDS } from '../kohd/lexicon';
import { ITEM_HALF } from '../kohd/layout';
import { chargeShapes, constructShapes, couplerShapes, groundShapes, nullShapes, punctuationShapes, ringShapes, Shape } from '../kohd/draw';
import KohdShapes from '../kohd/react/KohdShapes';

const EAST = { x: 1, y: 0 };
const SOUTH = { x: 0, y: 1 };
const node = { x: 0, y: 0, col: 0, row: 0, rings: 1, outerR: 17 };
const endBar = { x: -12, y: 0, dir: EAST };
const WORDS = { ...LEXICON_WORDS, ...LEXICON_PHRASES };
const SPLIT_WORDS: Partial<Record<ConstructType, string>> = { ifThen: 'if ... then', fromTo: 'from ... to' };

const LEXICON = (Object.keys(CONSTRUCT_LABELS) as ConstructType[]).flatMap((type) =>
  [false, true].map((plural) => ({
    type,
    plural,
    words: Object.keys(WORDS).filter((w) => WORDS[w].type === type && Boolean(WORDS[w].plural) === plural),
  })),
).filter((e) => e.words.length > 0 || (!e.plural && SPLIT_WORDS[e.type]));

const PARTS: Array<[string, Shape[]]> = [
  ['charge', chargeShapes({ node, port: { x: -21, y: 0 }, dir: EAST, end: { x: 21, y: 0 }, bar: { x: 29, y: 0, dir: EAST } })],
  ['ground', groundShapes({ node, port: { x: 0, y: -26 }, dir: SOUTH, lineEnd: { x: 0, y: 6 }, end: { x: 0, y: 16 }, groups: [], letters: [], dots: [{ x: 0, y: -10 }], bar: { x: 0, y: 24, dir: SOUTH } })],
  ['nullMod', nullShapes({ x: -10, y: 8, stubEnd: { x: 14, y: -16 }, tip: { x: 17.2, y: -19.2 } })],
  ['coupler', couplerShapes({ x: 14, y: -26, barHeight: 5, bars: [16, 24, 12, 30, 20, 26, 14, 22].map((length, i) => ({ y: -26 + i * 6.6, length })) })],
  ['the', ringShapes(node, 'the')],
  ['a', ringShapes(node, 'a')],
  ['comma', constructShapes('comma', 30)],
  ['period', punctuationShapes(endBar, 'period')],
  ['exclamation', punctuationShapes(endBar, 'exclamation')],
  ['question', punctuationShapes(endBar, 'question')],
];

function Swatch({ shapes }: { shapes: Shape[] }) {
  return <svg className="kohd-swatch" viewBox="-40 -30 80 60" aria-hidden="true"><KohdShapes shapes={shapes} /></svg>;
}

export default function KohdLegend() {
  const s = useStrings().kohd;
  const parts = s.parts as Record<string, readonly [string, string]>;

  return (
    <section className="kohd-legend" aria-labelledby="kohd-legend-title">
      <h2 id="kohd-legend-title">{s.legendTitle}</h2>
      <h3>{s.partsTitle}</h3>
      <ul className="kohd-legend-grid">
        {PARTS.map(([key, shapes]) => (
          <li key={key} className="kohd-legend-item">
            <Swatch shapes={shapes} />
            <div>
              <strong>{parts[key][0]}</strong>
              <span>{parts[key][1]}</span>
            </div>
          </li>
        ))}
      </ul>
      <h3>{s.lexiconTitle}</h3>
      <ul className="kohd-legend-grid">
        {LEXICON.map(({ type, plural, words }) => (
          <li key={`${type}${plural}`} className="kohd-legend-item">
            <Swatch shapes={constructShapes(type, ITEM_HALF[type], plural)} />
            <div>
              <strong>{CONSTRUCT_LABELS[type]}{plural ? ` (${s.plural})` : ''}</strong>
              <span>{(words.length > 0 ? words : [SPLIT_WORDS[type]]).join(', ')}</span>
            </div>
          </li>
        ))}
      </ul>
      <p className="kohd-credit">{s.credit}</p>
    </section>
  );
}
